import debounce from 'lodash/debounce'
import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { Action } from 'history'
import history from 'history/browser'
import { ROUTES } from '@constants/index'
import {
  resetWorkflow,
  showApp,
  setAppVisibleAllowed,
  appBeforeCloseCallbackSelector,
  navigateBackCallbackSelector,
} from '@redux/slices/workflowSlice'
import { resetForms } from '@redux/slices/formsSlice'
import { resetSubmitStatus } from '@redux/slices/claimSubmitSlice'
import { resetClaimStatus } from '@redux/slices/claimStatusSlice'
import { resetInsuranceMemberState } from '@redux/slices/insuranceMemberSlice'
import { resetPrescriptionFileInitialState } from '@redux/slices/prescriptionFileSlice'
import {
  clearAllowDirectNavigation,
  clearLocalBasePath,
  isAllowDirectNavigation,
  isLocationAllowedToProceed,
  shouldNavigateToResume,
} from '@utils/navigationUtil'
import { scrollToDrawerTop } from '@utils/scrollToDrawerTop'
import { useEnvironment } from '@hooks/useEnvironment'
import { useInvokeAction } from '@hooks/useInvokeAction'
import { useWebchat } from '@hooks/useWebchat'
import { useAnalytics } from '@hooks/useAnalytics'
import { useSessionInfo } from '@hooks/useSessionInfo'
import { AppNavigatePartialState, useAppNavigate } from '@hooks/useAppNavigate'
import { usePathBuilder } from '@hooks/usePathBuilder'
import { useAppDrawerState } from './useAppDrawerState'

type UseAppDrawerResult = {
  onClose: () => void
  onNavigateBack?: () => void
}

export const useAppDrawer = (): UseAppDrawerResult => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useAppNavigate()
  const buildPath = usePathBuilder()
  const { pushOpenEvent } = useAnalytics()
  const { brand, basePath, basePathSearch } = useEnvironment()
  const { isRestricted } = useSessionInfo()
  const { isVisible, appClosing, appVisibleAllowed, activeStep } = useAppDrawerState()
  const onBeforeClose = useSelector(appBeforeCloseCallbackSelector)
  const onNavigateBack = useSelector(navigateBackCallbackSelector)
  const notifyFormAction = useInvokeAction()
  const { invokeWebchat } = useWebchat()

  const locationState: AppNavigatePartialState = location.state
  const basePathWithSearch = `${basePath}${basePathSearch}`

  useEffect(() => {
    const openDrawer = () => {
      // resume to correct view on page reload
      if (!locationState?.isNavigated && shouldNavigateToResume()) {
        navigate(buildPath(ROUTES.START))
      }

      dispatch(showApp())
      pushOpenEvent()
      invokeWebchat('hide')
    }

    const handleLoginRedirect = () => {
      if (!isRestricted) {
        openDrawer()
      } else {
        // restore url to basePath if restricted
        if (location.pathname.includes(ROUTES.BASE)) {
          history.replace(basePathWithSearch)
        }
      }
    }

    const handleNonAllowedMatch = () => {
      if (appVisibleAllowed) return

      if (isAllowDirectNavigation()) {
        clearAllowDirectNavigation()
        dispatch(setAppVisibleAllowed())
        return
      }

      const isMatch = window.location.pathname.includes(ROUTES.BASE)

      // reloaded when drawer was open
      if (isMatch && isLocationAllowedToProceed()) {
        // causes effect to run again and open drawer
        dispatch(setAppVisibleAllowed())
        return
      }

      // reject direct url access or last path since reload was different
      if (isMatch) {
        history.replace(basePathWithSearch)
      }

      clearLocalBasePath()
    }

    const debouncedHandler = debounce(() => {
      const isMatch = location.pathname.includes(ROUTES.BASE)

      if (isMatch && !isVisible && !appClosing) {
        // allowed navigation or page reload while drawer open
        if (appVisibleAllowed || isVisible) {
          const loginRedirect = window.location.pathname.includes(ROUTES.LOGIN_REDIRECT)
          if (loginRedirect) {
            handleLoginRedirect()
          } else {
            openDrawer()
          }
        } else {
          handleNonAllowedMatch()
        }
      }
    })

    debouncedHandler()
    return () => debouncedHandler.cancel()
  }, [
    dispatch,
    navigate,
    buildPath,
    pushOpenEvent,
    invokeWebchat,
    locationState,
    basePathWithSearch,
    brand,
    location,
    appVisibleAllowed,
    isVisible,
    appClosing,
    isRestricted,
  ])

  const resetState = useCallback(() => {
    invokeWebchat('reset')
    dispatch(resetForms())
    dispatch(resetClaimStatus())
    dispatch(resetSubmitStatus())
    dispatch(resetInsuranceMemberState())
    dispatch(resetPrescriptionFileInitialState())
  }, [invokeWebchat, dispatch])

  const closeDrawer = useCallback(() => {
    dispatch(resetWorkflow())

    // replace our history with base path (e.g. cart or checkout)
    // so that back button will work correctly
    history.replace(basePathWithSearch)
  }, [dispatch, basePathWithSearch])

  /**
   * On back navigation, clean up state if drawer is not closed
   */
  useEffect(() => {
    if (!isVisible) return

    const unlisten = history.listen(({ location, action }) => {
      if (action === Action.Pop && isVisible) {
        // back navigation when drawer still open, need to reset state
        const pathMatch = location.pathname.includes(`${ROUTES.BASE}`)
        if (!pathMatch) {
          closeDrawer()
          resetState()
        }
      }
    })

    return unlisten
  }, [resetState, closeDrawer, isVisible])

  const cleanupOnClose = useCallback(() => {
    resetState()
    clearLocalBasePath()
  }, [resetState])

  useEffect(() => {
    if (isVisible && appClosing) {
      closeDrawer()
      cleanupOnClose()
    }
  }, [closeDrawer, cleanupOnClose, isVisible, appClosing])

  /**
   * Step navigation
   */
  useEffect(() => {
    if (isVisible && !appClosing) {
      scrollToDrawerTop()
      notifyFormAction()
      invokeWebchat('hide')
    }
  }, [notifyFormAction, invokeWebchat, activeStep, isVisible, appClosing])

  /**
   * Handle app drawer close button - invokes onBeforeClose to perform optional, view specific
   * cleanup.
   */
  const onClose = () => {
    closeDrawer()

    if (!onBeforeClose || typeof onBeforeClose !== 'function') {
      cleanupOnClose()
      return
    }

    onBeforeClose()
      .catch(error => console.warn('Error on app drawer before close', error))
      .finally(cleanupOnClose)
  }

  return {
    onClose,
    onNavigateBack,
  }
}
